import React from 'react';
import { Input } from '@fluentui/react-components';
import NewProjectWizardCard from './NewProjectWizardCard';
import styles from './css/index.module.css';
import checkboxCheck from './assets/checkbox-check.svg';
import infoIcon from './assets/info.svg';
import { ACCESS_FEATURES, VDR_PRESETS, aclIdsForDraft, createEmptyAccessDraft, normalizeFeatureKeys } from './accessGroupCatalog';

function RadioMark({ checked }) {
    return (
        <span className={`${styles.agRadio} ${checked ? styles.agRadioChecked : ''}`} aria-hidden="true">
            {checked ? <span className={styles.agRadioDot} /> : null}
        </span>
    );
}

const CreateAccessGroup = ({ initialDraft, workstreams = [], existingNames = [], saving = false, onSave, onCancel }) => {
    const [draft, setDraft] = React.useState(() => {
        const next = initialDraft || createEmptyAccessDraft();
        return { ...next, features: normalizeFeatureKeys(next.features) };
    });

    const patch = patchValue => setDraft(prev => ({ ...prev, ...patchValue }));

    const toggleFeature = key => {
        setDraft(prev => {
            const selected = prev.features.includes(key) ? prev.features.filter(item => item !== key) : [...prev.features, key];
            return { ...prev, features: selected };
        });
    };

    const toggleWorkstream = id => {
        setDraft(prev => {
            const selected = prev.selectedWorkstreamIds.includes(id) ? prev.selectedWorkstreamIds.filter(item => item !== id) : [...prev.selectedWorkstreamIds, id];
            return { ...prev, selectedWorkstreamIds: selected };
        });
    };

    const name = draft.name.trim();
    const duplicate = existingNames.some(item => item.toLowerCase() === name.toLowerCase());
    const hasWorkstreams = draft.workstreamMode === 'all' || draft.selectedWorkstreamIds.length > 0;
    const continueDisabled = !name || duplicate || !hasWorkstreams || saving;

    const scopeLabel =
        draft.workstreamMode === 'all'
            ? 'All workstreams'
            : workstreams
                  .filter(ws => draft.selectedWorkstreamIds.includes(ws.id))
                  .map(ws => ws.name)
                  .join(' · ') || 'None selected';

    const handleSave = () => {
        if (continueDisabled) return;
        onSave({
            ...draft,
            name,
            features: normalizeFeatureKeys(draft.features),
            acls: aclIdsForDraft(draft, workstreams),
        });
    };

    return (
        <NewProjectWizardCard
            formWide
            hideProgress
            showHeaderDivider
            title="Create access group"
            continueLabel="Create access group"
            continueDisabled={continueDisabled}
            continueLoading={saving}
            onContinue={handleSave}
            onBack={onCancel}
            backLabel="Cancel"
            backInline>
            <h2 className={styles.agSectionTitle}>Project workspace access</h2>
            <p className={styles.agSectionHint}>Choose which parts of Lens this group can use and which workstreams they can access.</p>

            <div className={styles.agField}>
                <label className={styles.fieldLabel} htmlFor="access-group-name">
                    Group name <span className={styles.required}>*</span>
                </label>{' '}
                <br></br>
                <Input
                    id="access-group-name"
                    className={styles.textInput}
                    style={{ width: '100%' }}
                    value={draft.name}
                    onChange={e => patch({ name: e.target.value })}
                    placeholder="e.g. Technology Advisers"
                    maxLength={100}
                    autoFocus
                />
                {duplicate ? <p className={styles.errorMessage}>A group with this name already exists.</p> : null}
            </div>

            <p className={styles.agGroupLabel}>Features</p>
            <div className={styles.agFeatureGrid}>
                {ACCESS_FEATURES.map(feature => {
                    const checked = draft.features.includes(feature.key);
                    return (
                        <label key={feature.key} className={styles.agCheckRow} onClick={() => toggleFeature(feature.key)}>
                            <button type="button" role="checkbox" aria-checked={checked} className={`${styles.wsCheckbox} ${checked ? styles.wsCheckboxChecked : ''}`}>
                                {checked ? <img src={checkboxCheck} alt="" width={11} height={11} className={styles.wsCheckboxCheck} /> : null}
                            </button>
                            <span className={styles.agCheckLabel}>{feature.label}</span>
                        </label>
                    );
                })}
            </div>

            <p className={styles.agGroupLabel}>Workstreams</p>
            <div className={styles.agRadioStack} role="radiogroup" aria-label="Workstream access">
                <button type="button" className={styles.agRadioRow} onClick={() => patch({ workstreamMode: 'all' })}>
                    <RadioMark checked={draft.workstreamMode === 'all'} />
                    <span>
                        <span className={styles.agCheckLabel}>All workstreams</span>
                        {draft.workstreamMode === 'all' ? <span className={styles.agRadioHint}>Includes new workstreams added to the project later.</span> : null}
                    </span>
                </button>
                <div className={styles.agRadioBlock}>
                    <button type="button" className={styles.agRadioRow} onClick={() => patch({ workstreamMode: 'selected' })}>
                        <RadioMark checked={draft.workstreamMode === 'selected'} />
                        <span className={styles.agCheckLabel}>Selected workstreams</span>
                    </button>
                    {draft.workstreamMode === 'selected' ? (
                        <span className={styles.agNestedChecks}>
                            {workstreams.map(ws => {
                                const checked = draft.selectedWorkstreamIds.includes(ws.id);
                                return (
                                    <label key={ws.id} className={styles.agCheckRow} onClick={() => toggleWorkstream(ws.id)}>
                                        <button type="button" role="checkbox" aria-checked={checked} className={`${styles.wsCheckbox} ${checked ? styles.wsCheckboxChecked : ''}`}>
                                            {checked ? <img src={checkboxCheck} alt="" width={11} height={11} className={styles.wsCheckboxCheck} /> : null}
                                        </button>
                                        <span className={styles.agCheckLabel}>{ws.name}</span>
                                    </label>
                                );
                            })}
                            <span className={styles.agRadioHint}>New workstreams added later are not included automatically.</span>
                        </span>
                    ) : null}
                </div>
            </div>

            <div className={styles.agDivider} />

            <h2 className={styles.agSectionTitle}>Additional access</h2>
            <p className={styles.agSectionHint}>Configure project-wide Intelligence access and VDR file/folder permissions separately.</p>

            <div className={styles.agInsightCard}>
                <div className={styles.agInsightCopy}>
                    <p className={styles.agInsightTitle}>
                        Intelligence Insights
                        <img
                            src={infoIcon}
                            alt=""
                            width={14}
                            height={14}
                            className={styles.agInfoIcon}
                            title="Allows members of this group to view project-wide Intelligence Insights across all workstreams."
                        />
                    </p>
                    <p className={styles.agInsightHint}>Allows members of this group to view project-wide Intelligence Insights across all workstreams.</p>
                </div>
                <label className={styles.agCheckRow} onClick={() => patch({ intelligence: !draft.intelligence })}>
                    <button
                        type="button"
                        role="checkbox"
                        aria-checked={draft.intelligence}
                        className={`${styles.wsCheckbox} ${draft.intelligence ? styles.wsCheckboxChecked : ''}`}>
                        {draft.intelligence ? <img src={checkboxCheck} alt="" width={11} height={11} className={styles.wsCheckboxCheck} /> : null}
                    </button>
                    <span className={styles.agCheckLabel}>Grant access</span>
                </label>
            </div>

            <div className={styles.agVdrCard}>
                <p className={styles.agInsightTitle}>VDR permissions</p>
                <p className={styles.agInsightHint}>
                    These settings apply only to VDR files and folders. They do not affect Information Requests, Observations, Project Management or other Lens features.
                </p>

                <div className={styles.agScopeBox}>
                    <p className={styles.agScopeLabel}>VDR workstream scope</p>
                    <p className={styles.agScopeValue}>{scopeLabel}</p>
                    <p className={styles.agRadioHint}>Inherited from workstream access above</p>
                </div>

                <div className={styles.agPresetList} role="radiogroup" aria-label="VDR permissions">
                    {VDR_PRESETS.map(preset => {
                        const selected = draft.vdrPreset === preset.key;
                        return (
                            <button
                                key={preset.key}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                className={`${styles.agPreset} ${selected ? styles.agPresetSelected : ''}`}
                                onClick={() => patch({ vdrPreset: preset.key })}>
                                <RadioMark checked={selected} />
                                <span className={styles.agPresetCopy}>
                                    <span className={styles.agPresetTitle}>{preset.title}</span>
                                    <span className={styles.agRadioHint}>{preset.can}</span>
                                    <span className={styles.agPresetMapping}>Preset mapping: {preset.mapping}</span>
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className={styles.agAdvanced}>
                    <p className={styles.agInsightTitle}>Advanced VDR permissions</p>
                    <p className={styles.agRadioHint}>Customise access for specific VDR workstreams, folders or files.</p>
                    <p className={styles.agAdvancedNote}>
                        Folder and file-level access can be customised after project setup. <span className={styles.agAdvancedLink}>Configure later in project.</span>
                    </p>
                </div>
            </div>
        </NewProjectWizardCard>
    );
};

export default CreateAccessGroup;
